// Docs — the per-project documentation surface, presented with the same
// Drive-style idiom as Apps/Agents/Workflows/Audit: a collapsible left rail
// with two tabs, a document pane on the right.
//
//   Specification  — the generated living spec of the project (type 'spec'):
//                    what it is, its data model, pipelines, workflows, one
//                    page per app/agent. Written by the project assistant on
//                    every build/change (audit-enforced), editable by hand.
//   Knowledge      — everything else in the project's knowledge base: concept
//                    notes the agents keep, plus ingested items (Slack
//                    digests, meeting summaries) as sources get connected.
//
// The STORE is the paired fork's vault (markdown + [[wikilinks]], the same
// entries the Flow Knowledge Graph renders); this page reads it through the
// thin /api/docs/engine proxy. Adding a new knowledge source (type) needs no
// change here — unknown types land in the Knowledge tab automatically.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useProject } from "../contexts/ProjectContext";
import { fetchData } from "../fetchapi/fetchapi";
import { useUiView } from "@/hooks/useUiView";
import {
  BookText, FileText, Loader2, RefreshCw, PanelLeftClose, PanelLeftOpen, Pencil, Plus, Sparkles, X, Check, MessagesSquare, Video, StickyNote, ClipboardList, History, Trash2, Inbox, Wand2, Paperclip, Download, EyeOff, RotateCcw, Mic, ChevronDown, ChevronRight, ListChecks,
} from "lucide-react";
import { createAppRequest } from "../services/apps";
import RequirementIntake from "../components/docs/RequirementIntake";
import { lineDiffRows, toHunks, diffStats, tooLargeToDiff } from "../components/docs/diff";

const eng = (action, params) =>
  fetchData("docs/engine", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action, params }) });

const rowsOf = (v) => {
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object") {
    for (const k of ["entries", "rows", "data", "results", "items"]) {
      if (Array.isArray(v[k])) return v[k];
    }
  }
  return [];
};

const fmt = (v) => {
  if (!v) return null;
  try { return new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return null; }
};

// [[slug]] / [[slug|alias]] → in-page markdown links the renderer intercepts.
const expandWikilinks = (body) =>
  String(body || "").replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (_, slug, alias) =>
    `[${alias || slug}](#doc=${encodeURIComponent(slug.trim())})`);

const TYPE_META = {
  spec: { label: "Specification", Icon: FileText },
  requirement: { label: "Requirements", Icon: ClipboardList },
  request: { label: "Captured requests", Icon: Inbox },
  digest: { label: "Digests", Icon: MessagesSquare },
  meeting: { label: "Meetings", Icon: Video },
};
const typeMeta = (t) => TYPE_META[t] || { label: "Notes", Icon: StickyNote };

// Markdown element styling on theme tokens (no typography plugin in the app).
const MD_COMPONENTS = (onDocLink) => ({
  a: ({ href, children }) => {
    const m = /^#doc=(.+)$/.exec(href || "");
    if (m) {
      return (
        <a
          href={href}
          className="text-nav-active no-underline hover:underline"
          onClick={(e) => { e.preventDefault(); onDocLink(decodeURIComponent(m[1])); }}
        >
          {children}
        </a>
      );
    }
    return <a href={href} target="_blank" rel="noreferrer" className="text-nav-active hover:underline">{children}</a>;
  },
  h1: ({ children }) => <h1 className="text-xl font-semibold text-page-title mt-6 mb-2 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-semibold text-page-title mt-5 mb-2 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-page-title mt-4 mb-1.5">{children}</h3>,
  p: ({ children }) => <p className="text-sm text-page-text leading-relaxed my-2">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1 text-sm text-page-text">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1 text-sm text-page-text">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-page-divider pl-3 my-2 text-page-muted">{children}</blockquote>,
  code: ({ inline, children, ...props }) =>
    inline !== false && !String(props.className || "").includes("language-") ? (
      <code className="text-[12px] px-1 py-0.5 rounded bg-card-border/40 font-mono">{children}</code>
    ) : (
      <code className="font-mono text-[12px]">{children}</code>
    ),
  pre: ({ children }) => <pre className="text-[12px] rounded-md border border-page-divider bg-card-border/20 p-3 my-2 overflow-x-auto">{children}</pre>,
  table: ({ children }) => (
    <div className="overflow-x-auto my-2">
      <table className="text-sm border-collapse [&_th]:border [&_th]:border-page-divider [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-page-divider [&_td]:px-2 [&_td]:py-1">{children}</table>
    </div>
  ),
  hr: () => <hr className="border-page-divider my-4" />,
});

// One rail row per document, mirroring the Apps/Agents/Audit row idiom.
function DocRow({ entry, active, onOpen }) {
  const { Icon } = typeMeta(entry.type);
  return (
    <button
      type="button"
      data-testid={`docs-row-${entry.slug}`}
      onClick={onOpen}
      className={
        "w-full text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 rounded-md text-sm " +
        (active ? "bg-nav-active/10 text-nav-active" : "bg-transparent text-page-text hover:bg-card-border/40")
      }
    >
      <Icon size={15} className="shrink-0" />
      <span className="min-w-0 flex flex-col">
        <span className="overflow-hidden text-ellipsis whitespace-nowrap font-medium">{entry.title || entry.slug}</span>
        <span className="text-xs text-page-muted overflow-hidden text-ellipsis whitespace-nowrap">{entry.slug}</span>
      </span>
    </button>
  );
}

// Editor for one entry (also used for "New page" with a blank draft).
function DocEditor({ draft, setDraft, isNew, saving, error, onSave, onCancel }) {
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-3" data-testid="docs-editor">
      <div className="flex items-center gap-2">
        <Pencil size={16} className="text-page-muted" />
        <h1 className="text-lg font-medium text-page-title">{isNew ? "New page" : `Edit: ${draft.title || draft.slug}`}</h1>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={onCancel} disabled={saving} data-testid="docs-editor-cancel"
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-page-bg disabled:opacity-50">
            <X size={13} /> Cancel
          </button>
          <button onClick={onSave} disabled={saving} data-testid="docs-editor-save"
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-page-bg disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save
          </button>
        </div>
      </div>
      {error ? <div className="text-sm text-red-600">{error}</div> : null}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <label className="text-xs text-page-muted flex flex-col gap-1">
          Slug {isNew ? "(kebab-case, permanent)" : ""}
          <input value={draft.slug} disabled={!isNew}
            onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value.toLowerCase().replace(/[^a-z0-9/_-]/g, "-") }))}
            className="text-sm px-2 py-1.5 rounded-md border border-page-divider bg-transparent text-page-text disabled:opacity-60"
            placeholder="spec-overview" />
        </label>
        <label className="text-xs text-page-muted flex flex-col gap-1">
          Title
          <input value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            className="text-sm px-2 py-1.5 rounded-md border border-page-divider bg-transparent text-page-text"
            placeholder="Project overview" />
        </label>
        <label className="text-xs text-page-muted flex flex-col gap-1">
          Type
          <input value={draft.type}
            onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}
            className="text-sm px-2 py-1.5 rounded-md border border-page-divider bg-transparent text-page-text"
            placeholder="spec | concept | digest | meeting" />
        </label>
      </div>
      <textarea
        value={draft.body}
        onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
        className="w-full min-h-[55vh] text-sm font-mono px-3 py-2 rounded-md border border-page-divider bg-transparent text-page-text leading-relaxed"
        placeholder={"Markdown. Link related pages with [[slug]] or [[slug|alias]]."}
        data-testid="docs-editor-body"
      />
    </div>
  );
}

// ── Requirements (the Story/Ticket Template surface) ─────────────────────────

const reqApi = (path, body) =>
  fetchData(`requirements/${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });

// The hand-authoring skeleton: the same sections the generator emits, so a
// requirement written by hand is the same shape as a generated one — the task
// extractor and the audit's fidelity check both read these headings by name.
const REQUIREMENT_SKELETON = `## Story

*Format: Job Story (When … I want to … so I can …)*

> When <situation>, I want to <motivation>, so I can <outcome>.

## Trigger criteria

### <scenario name>

\`\`\`gherkin
Given <precondition>
When <action>
Then <expected result>
\`\`\`

## Definition of Done

- [ ] <checkable completion criterion>

## Test plan

1. **<case name>** — <steps> → *expected:* <result>

## Sources

1. **[S1]** <where this requirement came from>

## Deviations

_None recorded. An implementation that has to differ from this document records the difference here (each entry mints a new revision)._
`;

// VERSIONS — one panel, git-log style: a row per version, newest first, with
// the material that produced it INLINE. This replaced two panels (Sources,
// Version history) that answered halves of the same question — "we're on v3:
// what came in, and what changed?" — in different places, so a file's version
// had to be found by matching labels across them.
//
// Selecting a version shows WHAT IT CHANGED — diffed against its PARENT, not
// against the live document (`git show <commit>`), because "what did this
// version do" is the question a history is asked. The base is then freely
// selectable (`git diff A B`), and the raw document at that version stays one
// click away.
const SOURCE_ICON = { audio: Mic, text: MessagesSquare, pdf: FileText, doc: FileText };

const fmtBytes = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// One attached file: what it is, how it read, open the original, retire it.
// Downloads are signed ON CLICK — a URL minted when the panel rendered would
// be expired by the time anyone used it.
function SourceRow({ s, busy, onOpen, onToggle }) {
  const Icon = SOURCE_ICON[s.kind] || FileText;
  return (
    <div data-testid={`req-source-${s.id}`}
      className={`flex items-center gap-2 text-xs rounded-md border border-page-divider/70 bg-card-border/10 px-2 py-1 ${s.excluded ? "opacity-50" : ""}`}>
      <Icon size={12} className="shrink-0 text-page-muted" />
      <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-page-text" title={s.name}>
        {s.name}
        {s.excluded ? <span className="ml-1.5 text-page-muted">(retired)</span> : null}
      </span>
      {s.status === "transcribing" || s.status === "parsing" ? (
        <span className="shrink-0 inline-flex items-center gap-1 text-page-muted">
          <Loader2 size={11} className="animate-spin" /> {s.status === "parsing" ? "reading" : "transcribing"}{s.progress ? ` ${s.progress}%` : "…"}
        </span>
      ) : s.status === "failed" ? (
        <span className="shrink-0 text-red-600" title={s.error || ""}>{s.error ? `failed — ${s.error}` : "failed"}</span>
      ) : (
        <span className="shrink-0 text-page-muted">
          {s.chars ? `${s.chars.toLocaleString()} chars` : ""}{s.bytes ? ` · ${fmtBytes(s.bytes)}` : ""}
        </span>
      )}
      {s.has_original ? (
        <button type="button" onClick={() => onOpen(s.id)} disabled={busy === s.id}
          title="Open the original file" data-testid={`req-source-open-${s.id}`}
          className="shrink-0 bg-transparent border-0 cursor-pointer text-page-muted hover:text-nav-active p-0.5 disabled:opacity-50">
          {busy === s.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
        </button>
      ) : null}
      <button type="button" onClick={() => onToggle(s)} disabled={busy === s.id}
        title={s.excluded ? "Use again in the next version" : "Retire — leave out of future versions (kept for the record)"}
        className="shrink-0 bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text p-0.5 disabled:opacity-50">
        {s.excluded ? <RotateCcw size={12} /> : <EyeOff size={12} />}
      </button>
    </div>
  );
}

// The contract that travels ON every card filed from a requirement: a worker
// that reads nothing but the card still knows the document is the authority.
const NEEDS_APP = new Set(["change", "bug_fix"]);

const TASK_CONTRACT =
  "Implement what the requirement document states. Ambiguity → block the task with a comment rather than " +
  "improvising. Completion requires the relevant Definition of Done items ticked and the Test plan cases run; " +
  "anything that had to differ from the document is recorded in its Deviations section (a new revision) before completing.";

function VersionsPanel({ slug, doc, projectSlug, initialRev = null, initialDiff = false, readingRev = null, onRead, jobs = [], onJobsChange, onAddMaterial }) {
  const [data, setData] = useState(null);      // { current_rev, app, dod, delivery, versions, pending }
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(() => new Set());   // expanded versions
  const [diffRev, setDiffRev] = useState(initialDiff ? initialRev : null);  // the ONE version whose diff is shown
  const [base, setBase] = useState(null);      // rev compared against (null = parent)
  const [mode, setMode] = useState("diff");    // 'diff' | 'full'
  const [bodies, setBodies] = useState({});    // rev -> body (rendered cache)
  const cache = React.useRef({});             // same, readable synchronously
  const [busy, setBusy] = useState(false);
  const [fileBusy, setFileBusy] = useState(null);
  const [fileErr, setFileErr] = useState("");
  const [taskBusy, setTaskBusy] = useState(null);      // task id being added / decided
  const [batchBusy, setBatchBusy] = useState(false);
  const [taskErr, setTaskErr] = useState("");
  const [taskOpen, setTaskOpen] = useState(() => new Set());  // task rows showing brief + evidence
  const [tasksShown, setTasksShown] = useState({});    // rev -> list folded/unfolded (overrides the default)
  const [selected, setSelected] = useState(() => new Set());  // task keys ticked for "Add selected"
  const [planning, setPlanning] = useState(false);
  const [note, setNote] = useState("");

  // Only the NEWEST request may write state. Polling and the reload that
  // follows a landed rebuild overlap by design, and without this the slower
  // (older) response won: the version just written disappeared from the list,
  // and the diff it was supposed to open on had no row to attach to.
  const loadSeq = React.useRef(0);
  const load = useCallback(() => {
    const seq = ++loadSeq.current;
    return fetchData(`requirements/${slug}/versions`, { method: "GET" })
      .then((r) => { if (seq === loadSeq.current) { setData(r || null); setErr(""); } })
      .catch((e) => { if (seq === loadSeq.current) setErr(String(e?.message || "history unavailable")); });
  }, [slug]);

  // Re-read when the document moves (a rebuild landed, an edit was saved):
  // doc.rev is the signal. The diff follows ?rev&diff=1 — the landing after
  // a rebuild — so a finished rebuild opens on what it changed.
  useEffect(() => {
    let alive = true;
    setData(null); setDiffRev(initialDiff ? initialRev : null); setBase(null); setErr(""); setSelected(new Set());
    // `mode` too: a reader who last clicked "View document" would otherwise
    // land on a rebuild showing the whole new text instead of what changed.
    setMode("diff");
    setBodies({}); cache.current = {};
    load().then(() => { if (!alive) return; });
    return () => { alive = false; };
  }, [slug, doc?.rev, initialRev, initialDiff, load]);

  // Which version starts unfolded: the one ?rev names, else the current one.
  // The reader's work is on the newest version; history stays folded until
  // asked. Keyed on current_rev, not on data, so a poll does not refold.
  useEffect(() => {
    if (!data?.current_rev) return;
    setOpen(new Set([initialRev || data.current_rev]));
  }, [slug, initialRev, data?.current_rev]);

  // Poll while a file is still being read, a version is being written, or a
  // filed task is still moving on the Board (its status is shown live here).
  const running = jobs.filter((j) => j.status === "running");
  const reading = (data?.pending || []).some((p) => p.status === "transcribing" || p.status === "parsing");
  const boardMoving = (data?.delivery?.open || 0) > 0;
  useEffect(() => {
    if (!reading && !running.length && !boardMoving) return undefined;
    const t = setInterval(load, boardMoving && !running.length && !reading ? 15000 : 4000);
    return () => clearInterval(t);
  }, [reading, running.length, boardMoving, load]);

  const versions = data?.versions || [];
  const parentOf = (rev) => (rev > 1 ? rev - 1 : null);
  const effectiveBase = diffRev == null ? null : (base ?? parentOf(diffRev));

  // Load whatever the current (diffRev, base) pair needs. Declarative on
  // purpose: fetching inside the click handlers raced its own state — one
  // failed fetch left `busy` pinned and the diff stuck on "Loading…".
  useEffect(() => {
    if (diffRev == null) { setBusy(false); return; }
    const wanted = [diffRev, effectiveBase].filter((r) => r != null && r >= 1);
    const missing = wanted.filter((r) => cache.current[r] == null);
    if (!missing.length) { setBusy(false); return; }
    let alive = true;
    setBusy(true);
    Promise.all(missing.map((rev) =>
      eng("revision", { slug, rev })
        .then((r) => ({ rev, body: r?.data?.body ?? "" }))
        .catch(() => ({ rev, body: null }))))
      .then((loaded) => {
        if (!alive) return;
        const failed = loaded.filter((x) => x.body == null).map((x) => x.rev);
        for (const { rev, body } of loaded) if (body != null) cache.current[rev] = body;
        setBodies({ ...cache.current });
        setErr(failed.length ? `Could not load rev ${failed.join(", ")}.` : "");
      })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [slug, diffRev, effectiveBase]);

  const toggleOpen = (rev) => setOpen((o) => { const n = new Set(o); if (n.has(rev)) n.delete(rev); else n.add(rev); return n; });
  const toggleDiff = (rev) => { if (diffRev === rev) { setDiffRev(null); setBase(null); } else { setDiffRev(rev); setBase(null); setMode("diff"); setErr(""); } };
  const changeBase = (value) => { setBase(value === "" ? null : Number(value)); setErr(""); };

  const openFile = async (id) => {
    setFileBusy(id); setFileErr("");
    try {
      const r = await fetchData(`requirements/sources/${id}/url`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: "{}",
      });
      if (r?.url) window.open(r.url, "_blank", "noopener");
    } catch (e) { setFileErr(String(e?.message || "could not open the original")); }
    finally { setFileBusy(null); }
  };
  // Retiring EXCLUDES a file from the next version without deleting it: the
  // record of what a version was built from must outlive someone changing
  // their mind about which document was authoritative.
  const toggleFile = async (row) => {
    setFileBusy(row.id); setFileErr("");
    try {
      await fetchData(`requirements/sources/${row.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ excluded: !row.excluded }),
      });
      await load();
    } catch (e) { setFileErr(String(e?.message || "could not update the file")); }
    finally { setFileBusy(null); }
  };
  const dismissJob = async (id) => {
    setErr("");
    try { await fetchData(`requirements/jobs/${id}`, { method: "DELETE" }); }
    catch (e) {
      // Only the person who started it may dismiss it, so this is a real
      // answer — not something to swallow and leave the row sitting there.
      setErr(/not found/i.test(String(e?.message || ""))
        ? "That was started by someone else — only they can dismiss it."
        : String(e?.message || "could not dismiss this job"));
    }
    onJobsChange?.();
  };
  // Material that is registered and readable but never made it into a version
  // (its rebuild failed, or the dialog closed before it ran): one click sends
  // it through the same path Add material uses.
  const rebuildNow = async () => {
    setFileErr("");
    try {
      await fetchData(`requirements/${slug}/rebuild`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: "{}",
      });
      onJobsChange?.();
    } catch (e) { setFileErr(String(e?.message || "could not start the rebuild")); }
  };

  // ── tasks: proposed by the planner when a version lands; a human decides ──
  const allTasks = versions.flatMap((v) => v.tasks || []);
  const currentVersion = versions.find((v) => v.live) || versions[0];
  // Proposals normally appear on their own when a version is written. This
  // is the fallback for a version that has none (the planner hiccuped, or the
  // document predates automatic proposals).
  const suggest = async () => {
    setPlanning(true); setTaskErr(""); setNote("");
    try {
      const r = await reqApi(`${slug}/extract_tasks`, {});
      // 'unchanged' returns the EXISTING ledger untouched (new_items: 0), so
      // counting its items would announce work that was planned long ago as
      // freshly suggested — under a version that shows none of it.
      const n = r?.mode === "delta" ? r.new_items
        : r?.mode === "unchanged" ? 0
        : (r?.items || []).length;
      setNote(n ? `${n} task(s) suggested — review them under rev ${r?.doc_rev || data?.current_rev}.` : "Nothing to suggest: the document does not describe work that is not already planned.");
      await load();
    } catch (e) { setTaskErr(String(e?.message || "could not suggest tasks")); }
    finally { setPlanning(false); }
  };
  const keyOf = (t) => t.id || t.idx;
  const isNew = (t) => String(t.kind || "").startsWith("new_");
  // The card that builds the app this requirement is about, if one has been
  // added already — later tasks depend on it.
  const builderCard = () => allTasks.find((t) => t.request_id && t.kind === "new_app") || null;
  // Add ONE task to the Board. The card cites the version the task was
  // planned from, so "which requirement, which version" survives on the card
  // even if this document moves on; `dependsOn` names the card that must be
  // done first (the app has to exist before anything can change it).
  const addTask = async (v, t, { reload = true, dependsOn = null } = {}) => {
    const key = keyOf(t);
    // A change to an app cannot be filed before the app exists — the Board
    // refuses it outright. Say so here instead of letting the request 400.
    if (NEEDS_APP.has(t.kind) && !data?.app?.id) {
      const e = new Error(`"${t.title}" changes the app, so it waits until the app exists — add the app task first.`);
      setTaskErr(e.message); throw e;
    }
    setTaskBusy(key); setTaskErr("");
    try {
      const dep = dependsOn || (!isNew(t) ? builderCard() : null);
      const details = [
        t.brief,
        "",
        `Requirement: [[${slug}]] @ rev ${t.from_rev || v.rev} — section "${t.section}"`,
        `Evidence: "${t.evidence}"`,
        ...(dep ? [`Depends on: #${dep.request_id} (${dep.title}) — do not start until it is done`] : []),
        "",
        TASK_CONTRACT,
      ].join("\n");
      // The planner chose the kind; an unknown one is normalized to triage
      // server-side, so whatever arrives here is safe to file.
      // Born QUEUED, not in the backlog. Adding it here IS the approval — the
      // review gate is this panel, and a card filed as `todo` would sit
      // untouched until someone dragged it, because workers only claim
      // 'queued' (apps/index.js). Two gates for one decision is one too many.
      const res = await createAppRequest({
        kind: t.kind || "triage", title: t.title, details,
        ...(NEEDS_APP.has(t.kind) && data?.app?.id ? { app_id: data.app.id } : {}),
      }, { projectSlug });
      if (!res?.id) throw new Error("request id missing in response");
      // Link by the item's STABLE id — a re-plan reshuffles idx.
      await reqApi(`${slug}/tasks/${key}/link`, { request_id: res.id });
      if (reload) await load();
      return { ...t, request_id: res.id };
    } catch (e) { setTaskErr(String(e?.message || "could not add the task")); throw e; }
    finally { setTaskBusy(null); }
  };
  const decide = async (t, rejected, { reload = true } = {}) => {
    const key = keyOf(t);
    setTaskBusy(key); setTaskErr("");
    try {
      await fetchData(`requirements/${slug}/tasks/${key}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rejected }),
      });
      if (reload) await load();
    } catch (e) { setTaskErr(String(e?.message || "could not record the decision")); throw e; }
    finally { setTaskBusy(null); }
  };
  const proposedOf = (v) => (v.tasks || []).filter((t) => !t.request_id && !t.rejected);
  // Waiting on the app rather than on a decision: the Board will not take it.
  const waitsForApp = (t) => NEEDS_APP.has(t.kind) && !data?.app?.id;
  // A batch goes to the Board in DELIVERY order: whatever builds something new
  // first, then the changes to it — each later card naming the builder card it
  // waits for. Nothing about the order is left for a worker to guess.
  const addMany = async (v, tasks) => {
    setBatchBusy(true); setTaskErr(""); setNote("");
    try {
      // Whatever builds something new goes first, then the changes to it —
      // each naming the builder card it waits for. Tasks the Board cannot
      // accept yet are set aside rather than failing the batch.
      const doable = tasks.filter((t) => !waitsForApp(t));
      const held = tasks.filter(waitsForApp);
      const ordered = [...doable.filter(isNew), ...doable.filter((t) => !isNew(t))];
      let builder = builderCard();
      let added = 0;
      let stopped = null;
      for (const t of ordered) {
        try {
          const made = await addTask(v, t, { reload: false, dependsOn: isNew(t) ? null : builder });
          added++;
          if (!builder && t.kind === "new_app") builder = made;
        } catch (e) { stopped = { title: t.title, why: String(e?.message || e) }; break; }
      }
      setSelected(new Set());
      await load();
      // One honest sentence: what went, what waits, and where it stopped.
      const parts = [];
      if (added) parts.push(`Added ${added} task(s) to the Board`);
      if (held.length) parts.push(`${held.length} wait${held.length === 1 ? "s" : ""} for the app to exist — add ${added ? "them" : "the app task"} once it is built and linked`);
      if (stopped) parts.push(`stopped at "${stopped.title}": ${stopped.why}`);
      setNote(parts.join(" · ") || "Nothing to add.");
    } finally { setBatchBusy(false); }
  };
  const rejectMany = async (tasks) => {
    setBatchBusy(true);
    try {
      for (const t of tasks) { try { await decide(t, true, { reload: false }); } catch { break; } }
      setSelected(new Set());
      await load();
    } finally { setBatchBusy(false); }
  };
  const toggleSelected = (key) => setSelected((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const targetBody = diffRev == null ? "" : (bodies[diffRev] ?? "");
  const baseBody = effectiveBase == null ? "" : (bodies[effectiveBase] ?? "");
  const rows = useMemo(
    () => (diffRev == null ? [] : lineDiffRows(baseBody, targetBody)),
    [diffRev, baseBody, targetBody]);
  const hunks = useMemo(() => toHunks(rows), [rows]);
  const stats = useMemo(() => diffStats(rows), [rows]);

  const who = (e) => (e ? String(e).split("@")[0] : "?");
  const pending = data?.pending || [];
  const pendingReady = pending.filter((p) => p.status === "ready" && !p.excluded);
  const del = data?.delivery || {};
  const anyBusy = taskBusy != null || batchBusy;
  const kindChip = (kind) => kind ? (
    <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] ${kind === "triage" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400" : "bg-card-border/40 text-page-muted"}`}
      title={kind === "triage" ? "The planner could not classify this — a human triages it on the Board" : `Goes to the Board as ${kind}`}>
      {kind}
    </span>
  ) : null;
  // The Board's word for where a card stands, coloured by whether it is
  // moving, waiting on someone, done, or stuck.
  const boardChip = (t) => {
    const st = t.board_status;
    const tone = st === "done" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
      : st === "running" || st === "claimed" ? "bg-nav-active/10 text-nav-active"
      : st === "queued" ? "bg-nav-active/10 text-nav-active"
      : st === "waiting" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
      : st === "failed" || st === "cancelled" ? "bg-red-500/10 text-red-700 dark:text-red-400"
      : "bg-card-border/40 text-page-muted";
    const label = st === "done" ? "✓ done"
      : st === "running" || st === "claimed" ? "in progress"
      : st === "queued" ? "queued for a worker"
      : st === "waiting" ? "blocked — needs you"
      : st === "failed" ? "failed"
      : st === "cancelled" ? "cancelled"
      : st === "todo" ? "in the backlog — start it on the Board"
      : st || "on the Board";
    return (
      <a href={`/p/${encodeURIComponent(projectSlug)}/b/main/board`}
        className={`shrink-0 px-1.5 py-0.5 rounded no-underline hover:underline ${tone}`}
        data-testid={`req-task-chip-${t.idx}`}
        title={`Board card #${t.request_id} — ${st || "filed"}. Open the Board.`}>
        #{t.request_id} · {label}
      </a>
    );
  };

  return (
    <div className="rounded-lg border border-page-divider p-3 mb-4 space-y-2" data-testid="req-history">
      <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-page-title">
        <History size={14} /> Versions
        {data ? <span className="text-xs font-normal text-page-muted">current rev {data.current_rev}</span> : null}
        <div className="ml-auto flex items-center gap-1.5">
          {data && currentVersion && !(currentVersion.tasks || []).length && !running.length ? (
            <button type="button" onClick={suggest} disabled={planning} data-testid="req-extract-tasks"
              title="Ask the planner what work this version calls for — you add or reject each suggestion"
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 disabled:opacity-50">
              {planning ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Suggest tasks for rev {data.current_rev}
            </button>
          ) : null}
          <button type="button" onClick={onAddMaterial} data-testid="req-add-sources"
            title="Attach a new document, remarks or a recording — the requirement is rebuilt into a new version and its new tasks are suggested"
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30">
            <Plus size={12} /> Add material
          </button>
        </div>
      </div>

      {/* DELIVERY: what this requirement is being executed against, and how
          far it has got. The app is where the work lands; the Board carries
          each task's state; the Definition of Done in the document is ticked
          by the workers as they finish. */}
      {data ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-page-divider/70 bg-card-border/10 px-2 py-1.5 text-xs" data-testid="req-delivery">
          <span className="text-page-muted">Delivered to:</span>
          {data.app?.view ? (
            <a href={`/p/${encodeURIComponent(projectSlug)}/b/main/apps/${encodeURIComponent(data.app.view)}`}
              className="font-medium text-nav-active hover:underline" data-testid="req-app-link">app {data.app.view}</a>
          ) : (
            <span className="text-page-text" title="A requirement can exist before its app. The first task of kind new_app builds the app; the worker links it here when it exists, and every later task lands on it.">
              no app yet — the <code className="text-[11px]">new_app</code> task builds it
            </span>
          )}
          <span className="text-page-muted">·</span>
          <span className="text-page-muted">Tasks:</span>
          {del.to_review ? <span className="text-amber-700 dark:text-amber-400">{del.to_review} to review</span> : null}
          {del.filed ? <span className="text-page-text">{del.filed} on the Board</span> : null}
          {del.todo ? <span className="text-page-muted">{del.todo} in the backlog</span> : null}
          {del.running ? <span className="text-nav-active">{del.running} in progress</span> : null}
          {del.waiting ? <span className="text-amber-700 dark:text-amber-400">{del.waiting} blocked</span> : null}
          {del.done ? <span className="text-emerald-700 dark:text-emerald-400">{del.done} done</span> : null}
          {del.failed ? <span className="text-red-700 dark:text-red-400">{del.failed} failed</span> : null}
          {del.rejected ? <span className="text-page-muted">{del.rejected} rejected</span> : null}
          {!allTasks.length ? <span className="text-page-muted">none yet</span> : null}
          {data.dod?.total ? (
            <>
              <span className="text-page-muted">·</span>
              <span className={data.dod.done === data.dod.total ? "text-emerald-700 dark:text-emerald-400" : "text-page-text"}
                title="Definition of Done items ticked in the document by the workers who completed them">
                DoD {data.dod.done}/{data.dod.total} ticked
              </span>
            </>
          ) : null}
        </div>
      ) : null}

      {/* The engine could not supply history. Everything else on this panel is
          real; only the older versions and the diffs are missing. */}
      {data?.history_unavailable ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs text-amber-800 dark:text-amber-300"
          data-testid="req-history-unavailable">
          Showing the current version only — this environment's engine does not report version history
          ({data.history_unavailable}). Sources, tasks and delivery are unaffected.
        </div>
      ) : null}
      {err ? <div className="text-xs text-red-600">{err}</div> : null}
      {fileErr ? <div className="text-xs text-red-600">{fileErr}</div> : null}
      {taskErr ? <div className="text-xs text-red-600" data-testid="req-task-error">{taskErr}</div> : null}
      {note ? <div className="text-xs text-page-muted" data-testid="req-extract-note">{note}</div> : null}
      {!data && !err ? <div className="text-xs text-page-muted flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Loading…</div> : null}

      {/* Work in flight on THIS document, where the reader is looking — not
          only in the list on the left. One failure at most: the newest. An
          older one is superseded the moment a version lands. */}
      {[...running, ...jobs.filter((j) => j.status === "failed").slice(0, 1)].map((j) => (
        <div key={`job-${j.id}`} data-testid={`req-doc-job-${j.id}`}
          className={`rounded-md border border-dashed px-2 py-1.5 text-xs ${j.status === "running" ? "border-page-divider" : "border-red-400/60"}`}>
          <div className="flex items-center gap-1.5">
            {j.status === "running"
              ? <Loader2 size={12} className="animate-spin shrink-0 text-page-muted" />
              : <X size={12} className="shrink-0 text-red-600" />}
            <span className="font-medium text-page-text">
              {j.status === "running" ? `writing rev ${(data?.current_rev || doc?.rev || 1) + 1}` : "the new version was not written"}
            </span>
            <span className={`min-w-0 flex-1 truncate ${j.status === "running" ? "text-page-muted" : "text-red-600"}`}>
              {j.status === "running"
                ? `from ${pending.length ? pending.map((p) => p.name).join(", ") : "its sources"}…`
                : j.error || "failed"}
            </span>
            {j.status !== "running" ? (
              <button type="button" title="Dismiss" onClick={() => dismissJob(j.id)} data-testid={`req-doc-job-dismiss-${j.id}`}
                className="shrink-0 bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text p-0.5"><X size={11} /></button>
            ) : null}
          </div>
        </div>
      ))}

      {/* Registered but not yet in any version. Normally empty: material is
          rebuilt into a version the moment it is attached. */}
      {pending.length && !running.length ? (
        <div className="rounded-md border border-dashed border-page-divider px-2 py-1.5 space-y-1" data-testid="req-pending-sources">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-page-muted">
            not yet in a version
            {pendingReady.length ? (
              <button type="button" onClick={rebuildNow} data-testid="req-rebuild-now"
                className="ml-auto inline-flex items-center gap-1 text-[11px] normal-case tracking-normal px-2 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-text">
                <RefreshCw size={10} /> Write the next version now
              </button>
            ) : null}
          </div>
          {pending.map((p) => <SourceRow key={p.id} s={p} busy={fileBusy} onOpen={openFile} onToggle={toggleFile} />)}
        </div>
      ) : null}

      {versions.map((v) => {
        const isOpen = open.has(v.rev);
        const ts = v.tasks || [];
        const proposed = ts.filter((t) => !t.request_id && !t.rejected);
        const filed = ts.filter((t) => t.request_id);
        const rejected = ts.filter((t) => t.rejected && !t.request_id);
        const picked = proposed.filter((t) => selected.has(keyOf(t)));
        // Split by what the BOARD can accept, not by what the user has decided:
        // a change to an app it has not built yet is refused outright.
        const held = proposed.filter(waitsForApp);
        const addable = proposed.filter((t) => !waitsForApp(t));
        // The task list unfolds on its own only while there is something to
        // decide; a fully-decided list is a record, and folds.
        const showTasks = tasksShown[v.rev] ?? proposed.length > 0;
        const showDiff = diffRev === v.rev;
        const isReading = readingRev === v.rev;
        return (
          <div key={v.rev} data-testid={`req-version-${v.rev}`}
            className={`rounded-md border ${isReading ? "border-amber-500/60" : isOpen ? "border-page-divider" : "border-page-divider/70"}`}>
            {/* Commit line, two rows: WHAT this version is (rev · who · when
                · the material that arrived · tasks to decide) and WHY (the
                change summary) — readable with everything folded. */}
            <div className="flex items-start gap-2 px-2 py-1.5">
              <button type="button" onClick={() => toggleOpen(v.rev)} data-testid={`req-rev-${v.rev}`}
                title={isOpen ? "Fold" : "Unfold — material, tasks, changes"}
                className="min-w-0 flex-1 text-left text-xs bg-transparent border-0 cursor-pointer p-0 space-y-0.5">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  {isOpen ? <ChevronDown size={12} className="shrink-0 text-page-muted" /> : <ChevronRight size={12} className="shrink-0 text-page-muted" />}
                  <span className="shrink-0 font-medium text-page-text">rev {v.rev}{v.live ? " (current)" : ""}</span>
                  <span className="shrink-0 text-page-muted">{who(v.updated_by)} · {fmt(v.updated) || "—"}</span>
                  {v.sources.map((sx) => (
                    <span key={sx.id} className="inline-flex items-center gap-1 max-w-[18rem] truncate rounded bg-card-border/30 px-1.5 py-0.5 text-[11px] text-page-text"
                      title={`${sx.name} — ${v.rev === 1 ? "the first version was written from this" : "added for this version"}`}>
                      <Paperclip size={10} className="shrink-0 text-page-muted" /> <span className="truncate">{sx.name}</span>
                    </span>
                  ))}
                  {ts.length ? (
                    <span className={`shrink-0 inline-flex items-center gap-1 text-[11px] ${proposed.length ? "text-amber-700 dark:text-amber-400" : "text-page-muted"}`}
                      title={`${ts.length} task(s) planned from this version · ${proposed.length} to review · ${filed.length} on the Board · ${rejected.length} rejected`}>
                      <ListChecks size={11} /> {proposed.length ? `${proposed.length} to review` : `${filed.length}/${ts.length} added`}
                    </span>
                  ) : null}
                </div>
                {v.summary || v.rev === 1 ? (
                  <div className="pl-5 text-[11px] text-page-muted line-clamp-2">{v.summary || "first version, written from the material above"}</div>
                ) : null}
              </button>
              {/* Read the document as it was at this version — the whole page
                  switches to it, with a banner and a way back. */}
              {onRead ? (
                isReading ? (
                  <button type="button" onClick={() => onRead(null)} data-testid={`req-read-${v.rev}`}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 cursor-pointer">
                    reading · back to current
                  </button>
                ) : v.live && !readingRev ? (
                  <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-md border border-page-divider/60 text-page-muted">reading</span>
                ) : (
                  <button type="button" onClick={() => onRead(v.rev)} data-testid={`req-read-${v.rev}`}
                    title={`Show the document as it was at rev ${v.rev}`}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-muted hover:text-page-text">
                    <BookText size={10} /> Read rev {v.rev}
                  </button>
                )
              ) : null}
            </div>

            {isOpen ? (
              <div className="border-t border-page-divider px-2 py-2 space-y-2" data-testid={`req-rev-artifacts-${v.rev}`}>
                {v.sources.length ? (
                  <div className="space-y-1">
                    <div className="text-[11px] uppercase tracking-wide text-page-muted">{v.rev === 1 ? "written from" : "new material in this version"}</div>
                    {v.sources.map((sx) => <SourceRow key={sx.id} s={sx} busy={fileBusy} onOpen={openFile} onToggle={toggleFile} />)}
                  </div>
                ) : null}
                {v.carried?.length ? (
                  <div className="text-[11px] text-page-muted" data-testid={`req-rev-carried-${v.rev}`}>
                    also built from: {v.carried.map((c) => `${c.name}${c.excluded ? " (retired)" : ""} · v${c.rev_added}`).join(" · ")}
                  </div>
                ) : null}

                {v.changes.length ? (
                  <div className="space-y-0.5">
                    <div className="text-[11px] uppercase tracking-wide text-page-muted">what changed</div>
                    <ul className="text-xs text-page-text list-disc pl-5 space-y-0.5" data-testid={`req-rev-changes-${v.rev}`}>
                      {v.changes.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                ) : null}

                {/* The work planned FROM this version — the artifact that
                    needs a decision per line. Nothing reaches the Board
                    without a click; a batch goes in delivery order. */}
                {ts.length ? (
                  <div className="space-y-1" data-testid={`req-rev-tasks-${v.rev}`}>
                    <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide text-page-muted">
                      <button type="button" onClick={() => setTasksShown((m) => ({ ...m, [v.rev]: !showTasks }))}
                        data-testid={`req-rev-tasks-toggle-${v.rev}`}
                        className="inline-flex items-center gap-1 bg-transparent border-0 cursor-pointer p-0 text-[11px] uppercase tracking-wide text-page-muted hover:text-page-text">
                        {showTasks ? <ChevronDown size={11} /> : <ChevronRight size={11} />} tasks from this version · {ts.length}
                      </button>
                      <span className="normal-case tracking-normal">
                        {proposed.length ? <span className="text-amber-700 dark:text-amber-400">{proposed.length} to review</span> : null}
                        {held.length ? <span className="text-page-muted"> · {held.length} wait for the app</span> : null}
                        {proposed.length && (filed.length || rejected.length) ? " · " : ""}
                        {filed.length ? `${filed.length} on the Board` : ""}{filed.length && rejected.length ? " · " : ""}{rejected.length ? `${rejected.length} rejected` : ""}
                      </span>
                      {proposed.length && showTasks ? (
                        <span className="ml-auto inline-flex items-center gap-1 normal-case tracking-normal">
                          <label className="inline-flex items-center gap-1 text-[11px] text-page-muted cursor-pointer mr-1" title="Select every task the Board can take now">
                            <input type="checkbox" className="cursor-pointer" data-testid={`req-select-all-${v.rev}`}
                              checked={addable.length > 0 && picked.length === addable.length}
                              onChange={(e) => setSelected((s) => { const n = new Set(s); for (const t of addable) { if (e.target.checked) n.add(keyOf(t)); else n.delete(keyOf(t)); } return n; })} />
                            select all
                          </label>
                          <button type="button" onClick={() => addMany(v, picked.length ? picked : proposed)} disabled={anyBusy || !addable.length} data-testid={`req-add-all-${v.rev}`}
                            title={held.length
                              ? `${held.length} task(s) change the app and wait until it exists — the rest go to the Board in delivery order`
                              : "Add to the Board in delivery order — what builds something new first, then the changes to it"}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-400 text-page-text disabled:opacity-50">
                            {batchBusy ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                            {picked.length ? `Add selected (${picked.length})` : `Add ${addable.length}`}
                          </button>
                          <button type="button" onClick={() => rejectMany(picked.length ? picked : proposed)} disabled={anyBusy} data-testid={`req-reject-all-${v.rev}`}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-red-500/10 hover:text-red-700 dark:hover:text-red-400 text-page-muted disabled:opacity-50">
                            <X size={10} /> {picked.length ? `Reject selected (${picked.length})` : "Reject all"}
                          </button>
                        </span>
                      ) : null}
                    </div>
                    {showTasks ? ts.map((t) => {
                      const key = keyOf(t);
                      const detail = taskOpen.has(key);
                      const working = taskBusy === key;
                      const open_ = !t.request_id && !t.rejected;
                      return (
                        <div key={key} data-testid={`req-task-${t.idx}`}
                          className={`rounded-md border px-2 py-1 text-xs ${t.rejected ? "border-page-divider/50 opacity-60" : selected.has(key) ? "border-nav-active/60 bg-nav-active/5" : "border-page-divider/70"}`}>
                          <div className="flex items-center gap-2">
                            {open_ && !waitsForApp(t) ? (
                              <input type="checkbox" className="shrink-0 cursor-pointer" checked={selected.has(key)} onChange={() => toggleSelected(key)}
                                data-testid={`req-task-select-${t.idx}`} title="Select for Add selected / Reject selected" />
                            ) : <span className="shrink-0 w-[13px]" />}
                            <button type="button" onClick={() => setTaskOpen((o) => { const n = new Set(o); if (n.has(key)) n.delete(key); else n.add(key); return n; })}
                              title={detail ? "Hide details" : "Show what this task is and where it comes from"}
                              className="shrink-0 bg-transparent border-0 cursor-pointer p-0 text-page-muted hover:text-page-text">
                              {detail ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                            </button>
                            <span className={`min-w-0 flex-1 truncate ${t.rejected ? "line-through text-page-muted" : "text-page-text"}`} title={t.title}>{t.title}</span>
                            {kindChip(t.kind)}
                            {t.request_id ? boardChip(t) : t.rejected ? (
                              <>
                                <span className="shrink-0 text-page-muted" title={t.rejected_by ? `rejected by ${t.rejected_by}` : "rejected"}>rejected</span>
                                <button type="button" onClick={() => decide(t, false)} disabled={anyBusy} title="Restore — propose it again"
                                  data-testid={`req-task-restore-${t.idx}`}
                                  className="shrink-0 bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text p-0.5 disabled:opacity-50">
                                  {working ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                                </button>
                              </>
                            ) : (
                              <>
                                {waitsForApp(t) ? (
                                  <span className="shrink-0 px-1.5 py-0.5 rounded bg-card-border/40 text-page-muted"
                                    data-testid={`req-task-waits-${t.idx}`}
                                    title="This changes the app, and the Board will not take it until the app exists. Add the app task first; once a worker has built and linked it, this becomes addable.">
                                    waits for the app
                                  </span>
                                ) : (
                                <button type="button" onClick={() => addTask(v, t).catch(() => {})} disabled={anyBusy}
                                  title="Add to the Board — a worker picks it up from there" data-testid={`req-task-file-${t.idx}`}
                                  className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-400 disabled:opacity-50">
                                  {working ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Add
                                </button>
                                )}
                                <button type="button" onClick={() => decide(t, true).catch(() => {})} disabled={anyBusy}
                                  title="Reject — keep it out of the plan (it will not be proposed again)" data-testid={`req-task-reject-${t.idx}`}
                                  className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer text-page-muted hover:bg-red-500/10 hover:text-red-700 dark:hover:text-red-400 disabled:opacity-50">
                                  <X size={11} /> Reject
                                </button>
                              </>
                            )}
                          </div>
                          {detail ? (
                            <div className="pl-10 pt-1 space-y-0.5 text-page-muted">
                              {t.brief ? <div>{t.brief}</div> : null}
                              {t.evidence ? <div className="italic text-[11px]">"{t.evidence}" <span className="not-italic">— {t.section || "document"}</span></div> : null}
                            </div>
                          ) : null}
                        </div>
                      );
                    }) : null}
                  </div>
                ) : null}

                {/* The diff is the heaviest artifact, so it opens on request
                    (or when a rebuild lands the reader here). */}
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <button type="button" onClick={() => toggleDiff(v.rev)} data-testid={`req-diff-toggle-${v.rev}`}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-muted hover:text-page-text">
                    {showDiff ? <ChevronDown size={11} /> : <ChevronRight size={11} />} {showDiff ? "Hide changes" : (parentOf(v.rev) ? `View changes vs rev ${parentOf(v.rev)}` : "View the first version")}
                  </button>
                  {showDiff ? (
                    <>
                      {mode === "diff" ? (
                        <>
                          <span className="text-emerald-700 dark:text-emerald-400 font-mono">+{stats.add}</span>
                          <span className="text-red-700 dark:text-red-400 font-mono">−{stats.del}</span>
                          <label className="inline-flex items-center gap-1 text-page-muted ml-1">
                            compared to
                            <select value={base ?? ""} onChange={(e) => changeBase(e.target.value)}
                              data-testid="req-diff-base"
                              className="text-xs px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text">
                              <option value="">{parentOf(v.rev) ? `rev ${parentOf(v.rev)} (parent)` : "empty (first version)"}</option>
                              {versions.filter((r) => r.rev !== v.rev).map((r) => (
                                <option key={r.rev} value={r.rev}>rev {r.rev}{r.live ? " (current)" : ""}</option>
                              ))}
                            </select>
                          </label>
                        </>
                      ) : null}
                      <button type="button" onClick={() => setMode(mode === "diff" ? "full" : "diff")}
                        data-testid="req-diff-mode"
                        className="ml-auto text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-muted hover:text-page-text">
                        {mode === "diff" ? "View document" : "View changes"}
                      </button>
                    </>
                  ) : null}
                </div>

                {showDiff ? (
                  busy ? (
                    <div className="text-xs text-page-muted flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Loading revision…</div>
                  ) : mode === "full" ? (
                    <pre className="text-[11px] leading-relaxed rounded-md border border-page-divider p-2 overflow-x-auto max-h-[45vh] overflow-y-auto font-mono whitespace-pre-wrap">{targetBody}</pre>
                  ) : tooLargeToDiff(baseBody, targetBody) ? (
                    <div className="text-xs text-page-muted rounded-md border border-page-divider p-3">
                      This document is too large to diff line-by-line. Use <b>View document</b> to read the revision in full.
                    </div>
                  ) : hunks.length === 0 ? (
                    <div className="text-xs text-page-muted rounded-md border border-page-divider p-3">
                      No textual changes between rev {effectiveBase ?? "∅"} and rev {v.rev}.
                    </div>
                  ) : (
                    <div className="text-[11px] leading-relaxed rounded-md border border-page-divider overflow-x-auto max-h-[45vh] overflow-y-auto font-mono"
                      data-testid="req-diff">
                      {hunks.map((h, hi) => (
                        <div key={hi}>
                          <div className="px-2 py-1 bg-card-border/30 text-page-muted sticky top-0">
                            @@ -{h.oldStart},{h.oldLines} +{h.newStart},{h.newLines} @@
                          </div>
                          {h.rows.map((r, ri) => (
                            <div key={ri} className={
                              "flex " + (r.kind === "add" ? "bg-emerald-500/10"
                                : r.kind === "del" ? "bg-red-500/10" : "")}>
                              <span className="shrink-0 w-10 px-1 text-right text-page-muted/60 select-none">{r.oldNo ?? ""}</span>
                              <span className="shrink-0 w-10 px-1 text-right text-page-muted/60 select-none border-r border-page-divider">{r.newNo ?? ""}</span>
                              <span className={
                                "shrink-0 w-4 text-center select-none " +
                                (r.kind === "add" ? "text-emerald-700 dark:text-emerald-400"
                                  : r.kind === "del" ? "text-red-700 dark:text-red-400" : "text-page-muted/50")}>
                                {r.kind === "add" ? "+" : r.kind === "del" ? "-" : " "}
                              </span>
                              <span className={
                                "whitespace-pre pr-2 " +
                                (r.kind === "add" ? "text-emerald-800 dark:text-emerald-300"
                                  : r.kind === "del" ? "text-red-800 dark:text-red-300" : "text-page-text")}>
                                {r.text || " "}
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// A CAPTURED REQUEST (auto-written when someone files a board task) — the raw
// ask. One click turns it into a specified requirement document: the raw text
// becomes the source, so every generated element still quotes the requester.
export default function Docs() {
  const { projectSlug } = useProject();
  // Simple view: Docs is read-only — the editor (slug/type/markdown/wikilinks)
  // and the generate/refresh-spec request flow are Advanced affordances.
  const { simple } = useUiView();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab = tabParam === "kb" ? "kb" : tabParam === "req" ? "req" : "spec";
  const docSlug = searchParams.get("doc") || null;
  // ?rev=N opens the document's history at that revision — this is the link
  // the Cortex chat hands back when it answers "which version added this".
  const revParam = Number(searchParams.get("rev")) || null;
  // ?rev=N reads the document AS IT WAS at rev N; ?diff=1 also opens what
  // that version changed (the landing after a rebuild).
  const diffParam = searchParams.get("diff") === "1";
  // The historic body, when ?rev names a version other than the live one.
  const [histDoc, setHistDoc] = useState(null);
  // null | { mode: 'new' } | { mode: 'add', slug, title }
  const [intake, setIntake] = useState(null);
  // Requirements being generated, and ones that failed. A document takes tens
  // of seconds to write, so it has to be VISIBLE while it is being written —
  // and a failure has to be readable rather than vanishing with the dialog.
  const [jobs, setJobs] = useState([]);
  const [jobErr, setJobErr] = useState("");

  const [entries, setEntries] = useState(null); // vault_list rows
  const [err, setErr] = useState("");
  const [doc, setDoc] = useState(null);         // loaded entry {slug,title,type,body,...}
  const [docLoading, setDocLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  // Snapshot of the draft as opened, so a navigation away can tell whether
  // there is unsaved work to warn about.
  const [draftBase, setDraftBase] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [msg, setMsg] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [liveJobs, setLiveJobs] = useState([]); // in-flight spec-generation requests for this project

  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem("docsRailOpen") !== "0"; } catch { return true; }
  });
  const toggleRail = () => setRailOpen((o) => { const n = !o; try { localStorage.setItem("docsRailOpen", n ? "1" : "0"); } catch {} return n; });

  const setParams = useCallback((next) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(next)) {
        if (v == null) p.delete(k); else p.set(k, v);
      }
      // ?rev pins ONE document's revision. Changing document must drop it, or
      // the next page opens at a revision belonging to the previous one.
      // Passing rev explicitly opts out. Handled here rather than at every
      // call site so a future one cannot forget.
      if ("doc" in next && !("rev" in next)) p.delete("rev");
      return p;
    }, { replace: false });
  }, [setSearchParams]);

  const loadReqJobs = useCallback(async () => {
    try {
      const r = await fetchData("requirements/jobs", { method: "GET" });
      setJobs(Array.isArray(r?.jobs) ? r.jobs : []);
    } catch { setJobs([]); }   // the list still works without job state
  }, []);

  const refresh = useCallback(async () => {
    setErr("");
    try {
      const r = await eng("list", {});
      setEntries(rowsOf(r?.data));
    } catch (e) {
      setEntries([]);
      setErr(String(e?.message || "The docs service is not reachable."));
    }
  }, []);
  useEffect(() => { setEntries(null); setDoc(null); refresh(); }, [projectSlug, refresh]);

  // In-flight spec generations for THIS project — the yellow strip. `all=1`
  // widens the listing for org admins (server-gated; silently degrades to
  // own-requests for plain members), so admin-filed generations show too.
  const loadJobs = useCallback(async () => {
    try {
      const r = await fetchData("app_requests?status=open&all=1", { method: "GET" });
      const jobs = (r?.requests || []).filter((x) =>
        x.project_slug === projectSlug &&
        ["queued", "claimed", "running"].includes(x.status) &&
        /specification/i.test(String(x.title || "")));
      setLiveJobs(jobs);
    } catch { setLiveJobs([]); }
  }, [projectSlug]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Requirement jobs: load once, then poll only while something is running.
  // When the running count drops the document has landed, so the list is
  // re-read — that is what makes it appear without a manual refresh.
  useEffect(() => { loadReqJobs(); }, [loadReqJobs, projectSlug]);
  const runningReqJobs = jobs.filter((j) => j.status === "running").length;
  const prevRunning = React.useRef(0);
  useEffect(() => {
    if (prevRunning.current > 0 && runningReqJobs < prevRunning.current) refresh();
    prevRunning.current = runningReqJobs;
  }, [runningReqJobs, refresh]);
  useEffect(() => {
    if (!runningReqJobs) return undefined;
    const t = setInterval(loadReqJobs, 4000);
    return () => clearInterval(t);
  }, [runningReqJobs, loadReqJobs]);
  // A rebuild of the OPEN document finishing: re-read it and land on the new
  // version's diff — the thing the material was added to see. Detected as a
  // running → done transition, so a job that was already done when the page
  // opened does not yank the reader anywhere.
  const seenJobs = React.useRef({});
  // Where the reader is NOW, readable from an async callback. The rebuild
  // landing below navigates, and a document switch during its round trip used
  // to snap the page back to the rebuilt document.
  const docSlugRef = React.useRef(docSlug);
  useEffect(() => { docSlugRef.current = docSlug; }, [docSlug]);
  useEffect(() => {
    for (const j of jobs) {
      const was = seenJobs.current[j.id];
      seenJobs.current[j.id] = j.status;
      if (was !== "running" || j.status !== "done" || j.kind !== "rebuild") continue;
      if (!j.doc_slug || j.doc_slug !== docSlug) continue;
      eng("get", { slug: j.doc_slug })
        .then((r) => {
          // The reader may have opened another document while this was in
          // flight; landing them back here would discard that navigation.
          if (docSlugRef.current !== j.doc_slug) return;
          const fresh = r?.data && !Array.isArray(r.data) ? r.data : null;
          if (fresh) setDoc(fresh);
          const rev = j.result?.rev || fresh?.rev;
          setParams({ tab: "req", doc: j.doc_slug, rev: rev ? String(rev) : null, diff: rev ? "1" : null });
        })
        .catch(() => {});
    }
  }, [jobs, docSlug, setParams]);
  // While something is running, poll it and reload the page list when it ends.
  useEffect(() => {
    if (!liveJobs.length) return;
    const t = setInterval(async () => {
      await loadJobs();
    }, 8000);
    return () => clearInterval(t);
  }, [liveJobs.length, loadJobs]);
  const hadJobs = React.useRef(false);
  useEffect(() => {
    if (liveJobs.length) { hadJobs.current = true; return; }
    if (hadJobs.current) { hadJobs.current = false; refresh(); } // a generation just finished → new pages
  }, [liveJobs.length, refresh]);

  // Reading an earlier version: fetch it when ?rev points away from the live
  // rev; drop it the moment they agree again.
  useEffect(() => {
    if (!docSlug || !revParam || !doc?.rev || revParam === doc.rev || doc.type !== "requirement") { setHistDoc(null); return; }
    let alive = true;
    eng("revision", { slug: docSlug, rev: revParam })
      .then((r) => { if (alive) setHistDoc(r?.data && !Array.isArray(r.data) ? r.data : null); })
      .catch(() => { if (alive) setHistDoc(null); });
    return () => { alive = false; };
  }, [docSlug, revParam, doc?.rev, doc?.type]);

  // Load the selected document.
  useEffect(() => {
    if (!docSlug || docSlug === "__new__") { setDoc(null); return; }
    let alive = true;
    setDocLoading(true); setEditing(false); setSaveErr("");
    eng("get", { slug: docSlug })
      .then((r) => { if (alive) setDoc(r?.data && !Array.isArray(r.data) ? r.data : null); })
      .catch(() => { if (alive) setDoc(null); })
      .finally(() => { if (alive) setDocLoading(false); });
    return () => { alive = false; };
  }, [docSlug, projectSlug]);

  const specEntries = useMemo(() => {
    const rows = (entries || []).filter((e) => e.type === "spec" || /^spec(-|$)/.test(String(e.slug || "")));
    // spec-overview pinned first, the rest alphabetical by slug.
    return rows.sort((a, b) => (a.slug === "spec-overview" ? -1 : b.slug === "spec-overview" ? 1 : String(a.slug).localeCompare(String(b.slug))));
  }, [entries]);

  const isReqEntry = (e) => e.type === "requirement" || /^req-/.test(String(e.slug || ""));
  // Captured requests (auto-written on every board filing) live on the
  // Requirements tab too — they are the raw origin end of the same chain.
  // Automatic capture is gone, but request-* pages written while it existed
  // are still in older project vaults. They are kept OUT of every tab rather
  // than surfacing in Knowledge as unexplained pages.
  const isCapturedRequest = (e) => e.type === "request" || /^request-\d+$/.test(String(e.slug || ""));
  const reqEntries = useMemo(() => {
    // Requirements newest-activity is not derivable from vault_list (no
    // `updated` there) — alphabetical by title, stable and predictable.
    return (entries || []).filter(isReqEntry)
      .sort((a, b) => String(a.title || a.slug).localeCompare(String(b.title || b.slug)));
  }, [entries]);

  const kbGroups = useMemo(() => {
    const rows = (entries || []).filter((e) =>
      !(e.type === "spec" || /^spec(-|$)/.test(String(e.slug || ""))) && !isReqEntry(e) && !isCapturedRequest(e));
    const groups = new Map(); // label -> rows
    for (const e of rows) {
      const { label } = typeMeta(e.type);
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(e);
    }
    for (const [label, list] of groups) {
      // Ingested items (digests/meetings) newest-first by slug (date-suffixed);
      // notes alphabetical by title.
      if (label === "Digests" || label === "Meetings") list.sort((a, b) => String(b.slug).localeCompare(String(a.slug)));
      else list.sort((a, b) => String(a.title || a.slug).localeCompare(String(b.title || b.slug)));
    }
    const order = ["Digests", "Meetings", "Notes"];
    return [...groups.entries()].sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
  }, [entries]);

  const visible = tab === "spec" ? specEntries
    : tab === "req" ? reqEntries
    : kbGroups.flatMap(([, list]) => list);
  const isNew = docSlug === "__new__";

  const startEdit = () => {
    if (!doc) return;
    setDraft({
      slug: doc.slug, title: doc.title || doc.slug,
      type: doc.type || (tab === "spec" ? "spec" : tab === "req" ? "requirement" : "concept"),
      body: doc.body || "",
      // Versioned types: carry the rev this edit is based on so a concurrent
      // edit is rejected instead of clobbered. A requirement written before
      // versioning has no `rev`; the server treats that as rev 1, so assert
      // it rather than leaving the legacy docs with NO protection at all.
      ...(doc.type === "requirement" ? { base_rev: doc.rev || 1 } : {}),
    });
    setDraftBase(JSON.stringify({ title: doc.title || doc.slug, body: doc.body || "" }));
    setSaveErr(""); setEditing(true);
  };
  const startNew = () => {
    setDraft({
      slug: tab === "spec" ? "spec-" : tab === "req" ? "req-" : "",
      title: "",
      type: tab === "spec" ? "spec" : tab === "req" ? "requirement" : "concept",
      // A hand-written requirement gets the same section skeleton the
      // generator produces — otherwise "New page" yields a requirement with
      // none of the parts that make it one (and the task extractor, which
      // reads those sections, has nothing to work with).
      body: tab === "req" ? REQUIREMENT_SKELETON : "",
    });
    setDraftBase(JSON.stringify({ title: "", body: tab === "req" ? REQUIREMENT_SKELETON : "" }));
    setSaveErr(""); setEditing(true);
    setParams({ doc: "__new__" });
  };
  const saveDraft = async () => {
    if (!draft?.slug || !draft?.title) { setSaveErr("Slug and title are required."); return; }
    // A requirement needs a KEY after the prefix: the seeded slug is just
    // "req-", and saving it as-is produces a document the requirements routes
    // reject (extract tasks, the ledger and linking all require req-<key>).
    if (draft.type === "requirement" && !/^req-[a-z0-9][a-z0-9-]*$/.test(draft.slug)) {
      setSaveErr('Add a key after "req-" — e.g. req-monthly-export (lowercase letters, digits and dashes).');
      return;
    }
    setSaving(true); setSaveErr("");
    try {
      await eng("upsert", {
        slug: draft.slug, title: draft.title, type: draft.type || "concept", body: draft.body || "",
        ...(draft.base_rev ? { base_rev: draft.base_rev } : {}),
      });
      setEditing(false);
      setParams({ doc: draft.slug });
      await refresh();
      // Re-load the saved doc (the get effect keys off docSlug, which may be unchanged).
      const r = await eng("get", { slug: draft.slug }).catch(() => null);
      if (r?.data && !Array.isArray(r.data)) setDoc(r.data);
    } catch (e) {
      setSaveErr(String(e?.message || "Failed to save."));
    } finally {
      setSaving(false);
    }
  };

  // Queue a spec (re)generation to the PROJECT ASSISTANT — the same
  // app_requests path the Audit page uses for fixes; the assistant's contract
  // (and the prj.specification audit check) owns the actual writing.
  const refreshSpec = async () => {
    setRefreshing(true); setMsg("");
    try {
      const r = await fetchData("app_requests", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // change_project: a project-level change with no app target —
          // plain 'change' requires an app_id and 400s.
          kind: "change_project",
          title: "Refresh the project specification",
          details: [
            "Regenerate/refresh this project's SPECIFICATION pages in the fork Knowledge Base (vault entries, type 'spec'):",
            "",
            "- `spec-overview` — what the project is and does, key decisions, change history (from the request briefs).",
            "- `spec-data-model` — sources, data elements, mappings, rules, targets.",
            "- `spec-pipelines` — the transformation graph, when it runs, what lands where.",
            "- `spec-workflows` — each business process, its actors and gates (if any).",
            "- `spec-app-<name>` — one page per published app; `spec-agent-<name>` per agent (if any).",
            "",
            "Derive content from the ACTUAL current state (project_status, sources, elements, workflows, apps, agents) plus the request history. Wikilink pages to each other and from the `project` note. Do not touch anything else.",
          ].join("\n"),
        }),
      });
      const id = (r?.request || r)?.id;
      setMsg(`Queued to the project assistant (request #${id}) — pages appear here as it completes; track it on the Board.`);
      loadJobs(); // show the strip immediately
    } catch (e) {
      setMsg(String(e?.message || "Failed to queue the request."));
    } finally {
      setRefreshing(false);
    }
  };

  const onDocLink = useCallback((slug) => setParams({ doc: slug }), [setParams]);
  const mdComponents = useMemo(() => MD_COMPONENTS(onDocLink), [onDocLink]);

  const TabBtn = ({ id, label }) => (
    <button
      type="button"
      data-testid={`docs-tab-${id}`}
      onClick={() => setParams({ tab: id === "spec" ? null : id, doc: null })}
      className={
        "flex-1 text-xs font-medium px-2 py-1.5 rounded-md border-0 cursor-pointer " +
        (tab === id ? "bg-nav-active/10 text-nav-active" : "bg-transparent text-page-muted hover:text-page-text")
      }
    >
      {label}
    </button>
  );

  return (
    <div className="flex h-full min-h-0" data-testid="docs-page">
      {!railOpen && (
        <div className="w-10 shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col items-center py-3 gap-2">
          <button type="button" data-testid="docs-rail-open" onClick={toggleRail} title="Show documents"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <PanelLeftOpen size={18} />
          </button>
          <BookText size={16} className="text-nav-active mt-1" />
        </div>
      )}

      {/* Left rail: Specification / Knowledge explorer (collapsible) */}
      <aside className={(railOpen ? "w-72" : "hidden") + " shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0"}>
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <BookText size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">Docs</span>
          <button type="button" data-testid="docs-refresh" onClick={refresh} title="Refresh"
            className="ml-auto text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <RefreshCw size={14} />
          </button>
          <button type="button" data-testid="docs-rail-close" onClick={toggleRail} title="Collapse"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <PanelLeftClose size={16} />
          </button>
        </div>

        <div className="px-2 pt-2 flex gap-1">
          <TabBtn id="spec" label="Specification" />
          <TabBtn id="req" label="Requirements" />
          <TabBtn id="kb" label="Knowledge" />
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {liveJobs.length > 0 && (
            <div className="flex items-center gap-2 text-xs rounded-md px-3 py-2 mb-1" data-testid="docs-live-jobs"
              style={{ background: "var(--severity-warning-bg,#fef3c7)", color: "var(--severity-warning-text,#92400e)" }}>
              <Loader2 size={13} className="animate-spin shrink-0" />
              <span className="flex-1 min-w-0">
                {liveJobs.length === 1
                  ? `Specification generation ${liveJobs[0].status === "queued" ? "queued" : "running"}…`
                  : `${liveJobs.length} generations in flight…`}
              </span>
              <button
                type="button"
                data-testid="docs-cancel-generation"
                title="Cancel generation"
                onClick={async () => {
                  try {
                    await fetchData(`app_requests/${liveJobs[0].id}`, { method: "DELETE" });
                    setMsg("Generation cancelled.");
                  } catch (e) {
                    setMsg(String(e?.message || "Could not cancel — running jobs can be cancelled by an org admin (or after 30 min)."));
                  }
                  loadJobs();
                }}
                className="bg-transparent border-0 cursor-pointer p-0.5 shrink-0"
                style={{ color: "inherit" }}
              >
                <X size={13} />
              </button>
            </div>
          )}
          {entries == null && (
            <div className="flex items-center gap-2 text-sm text-page-muted px-2 py-3">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          )}
          {entries != null && tab === "spec" && (
            specEntries.length === 0 ? (
              <div className="text-sm text-page-muted px-2 py-2">
                No specification yet — generate it below.
              </div>
            ) : specEntries.map((e) => (
              <DocRow key={e.slug} entry={e} active={docSlug === e.slug} onOpen={() => setParams({ doc: e.slug })} />
            ))
          )}
          {entries != null && tab === "req" && (
            <>
              {reqEntries.length === 0 ? (
                <div className="text-sm text-page-muted px-2 py-2">
                  No requirement documents yet — generate one below, or promote a
                  captured request.
                </div>
              ) : reqEntries.map((e) => (
                <DocRow key={e.slug} entry={e} active={docSlug === e.slug} onOpen={() => setParams({ doc: e.slug })} />
              ))}
              {/* Work in flight and work that failed. A document takes tens of
                  seconds to write; this is where you see that it is happening,
                  and where a failure states its reason instead of disappearing
                  with the dialog that started it. */}
              {jobs.filter((j) => j.status !== "done").map((j) => (
                <div key={`job-${j.id}`} data-testid={`req-job-${j.id}`}
                  className="px-2 py-1.5 rounded-md border border-dashed border-page-divider mb-1">
                  <div className="flex items-center gap-1.5 text-sm">
                    {j.status === "running"
                      ? <Loader2 size={12} className="animate-spin shrink-0 text-page-muted" />
                      : <X size={12} className="shrink-0 text-red-600" />}
                    <span className="min-w-0 flex-1 truncate text-page-text">
                      {j.title || (j.kind === "rebuild" ? "Updating requirement" : "New requirement")}
                    </span>
                    {j.status !== "running" && (
                      <button type="button" title="Dismiss" data-testid={`req-job-dismiss-${j.id}`}
                        onClick={async () => {
                          setJobErr("");
                          try { await fetchData(`requirements/jobs/${j.id}`, { method: "DELETE" }); }
                          catch (e) {
                            setJobErr(/not found/i.test(String(e?.message || ""))
                              ? "That was started by someone else — only they can dismiss it."
                              : String(e?.message || "could not dismiss this job"));
                          }
                          loadReqJobs();
                        }}
                        className="shrink-0 bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text p-0.5">
                        <X size={11} />
                      </button>
                    )}
                  </div>
                  <div className={`text-[11px] mt-0.5 ${j.status === "running" ? "text-page-muted" : "text-red-600"}`}>
                    {j.status === "running"
                      ? (j.kind === "rebuild" ? "rebuilding from its sources…" : "reading the sources and writing the document…")
                      : j.error || "failed"}
                  </div>
                  {jobErr ? <div className="text-[11px] mt-0.5 text-red-600">{jobErr}</div> : null}
                </div>
              ))}
            </>
          )}
          {entries != null && tab === "kb" && (
            kbGroups.length === 0 ? (
              <div className="text-sm text-page-muted px-2 py-2">
                Nothing here yet. Agent concept notes, Slack digests and meeting
                summaries land here as sources get connected.
              </div>
            ) : kbGroups.map(([label, list]) => (
              <div key={label} className="mb-2">
                <div className="px-2 pt-2 pb-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">{label}</span>
                </div>
                {list.map((e) => (
                  <DocRow key={e.slug} entry={e} active={docSlug === e.slug} onOpen={() => setParams({ doc: e.slug })} />
                ))}
              </div>
            ))
          )}
        </div>

        {/* Rail footer: primary actions, mirroring the Audit/Workflows idiom. */}
        {!simple && (
        <div className="p-2 border-t border-card-border space-y-1.5">
          {tab === "req" && (
            <>
              {/* Ask lives on the tab's overview; without this the panel is
                  unreachable once a document is open (the only way back was
                  re-clicking an already-active tab). */}
              <button type="button" data-testid="docs-new-requirement" onClick={() => setIntake({ mode: "new" })}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer">
                <Sparkles size={14} /> New requirement
              </button>
            </>
          )}
          {tab === "spec" && (
            <button type="button" data-testid="docs-refresh-spec" onClick={refreshSpec} disabled={refreshing}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer disabled:opacity-50">
              {refreshing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {specEntries.length ? "Refresh with assistant" : "Generate specification"}
            </button>
          )}
          {/* Requirements are BUILT FROM SOURCES, never typed from a blank
              template: a hand-written one has no originals behind it, so it
              cannot be rebuilt when remarks arrive and every evidence quote in
              it is unbacked. The intake dialog is the only door. Corrections
              go through Cortex AI or the editor on an existing document. */}
          {tab !== "req" && (
            <button type="button" data-testid="docs-new-page" onClick={startNew}
              title="Create a blank markdown page"
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer">
              <Plus size={14} /> New page
            </button>
          )}
        </div>
        )}
      </aside>

      {/* Main pane */}
      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto bg-page-bg">
        {editing && draft ? (
          <DocEditor draft={draft} setDraft={setDraft} isNew={isNew} saving={saving} error={saveErr}
            onSave={saveDraft} onCancel={() => { setEditing(false); if (isNew) setParams({ doc: null }); }} />
        ) : docSlug && !isNew ? (
          <div className="p-6 max-w-4xl mx-auto" data-testid="docs-doc">
            {docLoading ? (
              <div className="flex items-center gap-2 text-sm text-page-muted"><Loader2 size={16} className="animate-spin" /> Loading…</div>
            ) : !doc ? (
              <div className="text-sm text-page-muted">This page could not be loaded.</div>
            ) : (
              <>
                <div className="flex items-start gap-2 mb-1">
                  <h1 className="text-xl font-medium text-page-title">{doc.title || doc.slug}</h1>
                  {!simple && !histDoc && (
                    <button onClick={startEdit} data-testid="docs-edit"
                      className="ml-auto inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 shrink-0">
                      <Pencil size={13} /> Edit
                    </button>
                  )}
                </div>
                <div className="text-xs text-page-muted mb-4">
                  {doc.type || "note"} · {doc.slug}
                  {doc.rev ? ` · rev ${doc.rev}` : ""}
                  {fmt(doc.updated_at || doc.updated) ? ` · updated ${fmt(doc.updated_at || doc.updated)}` : ""}
                  {doc.updated_by ? ` by ${doc.updated_by}` : ""}
                </div>
                {doc.type === "requirement" && (
                  <>
                    <VersionsPanel slug={doc.slug} doc={doc} projectSlug={projectSlug}
                      initialRev={revParam} initialDiff={diffParam}
                      readingRev={histDoc ? revParam : null}
                      onRead={(rev) => setParams({ tab: "req", doc: doc.slug, rev: rev && rev !== doc.rev ? String(rev) : null, diff: null })}
                      jobs={jobs.filter((j) => j.doc_slug === doc.slug)}
                      onJobsChange={loadReqJobs}
                      onAddMaterial={() => setIntake({ mode: "add", slug: doc.slug, title: doc.title })} />
                  </>
                )}
                {histDoc ? (
                  <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
                    data-testid="docs-reading-rev">
                    <History size={13} />
                    <span>You are reading <b>rev {revParam}</b> of {doc.rev} — as it was on {fmt(histDoc.updated) || "—"}{histDoc.updated_by ? ` (${histDoc.updated_by})` : ""}. Editing is disabled here.</span>
                    <button type="button" onClick={() => setParams({ tab: "req", doc: doc.slug, rev: null, diff: null })} data-testid="docs-back-to-current"
                      className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-amber-500/40 bg-transparent cursor-pointer hover:bg-amber-500/10">
                      Back to current (rev {doc.rev})
                    </button>
                  </div>
                ) : null}
                <div data-testid="docs-doc-body" className={histDoc ? "opacity-90" : ""}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{expandWikilinks((histDoc ? histDoc.body : doc.body) ?? doc.text ?? "")}</ReactMarkdown>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="p-6 max-w-4xl mx-auto space-y-4" data-testid="docs-overview">
            <div>
              <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
                <BookText size={20} /> {tab === "spec" ? "Specification" : tab === "req" ? "Requirements" : "Knowledge Base"}
              </h1>
              <div className="text-sm text-page-muted mt-1">
                {tab === "spec"
                  ? <>the living specification of <b>{projectSlug}</b> — generated from the project's build/change requests and kept current by the project assistant on every change</>
                  : tab === "req"
                  ? <>prescriptive requirement documents for <b>{projectSlug}</b> — standardized Story/Ticket Template (Story + Gherkin trigger criteria + Definition of Done + Test plan), versioned, with every element traced to its source</>
                  : <>the project's knowledge base — concept notes from the agents, plus digests from connected sources (Slack, meetings) as they come online</>}
              </div>
            </div>
            {msg ? <div className="text-xs text-page-muted">{msg}</div> : null}
            {err ? <div className="text-sm text-red-600">{err}</div> : null}
            {entries != null && visible.length > 0 && (
              <div className="text-sm text-page-muted">
                {visible.length} page(s) — pick one on the left{tab === "spec" ? ", or refresh the specification below-left." : "."}
              </div>
            )}
            {entries != null && visible.length === 0 && !err && (
              tab === "req" ? (
                <div className="rounded-lg border border-page-divider p-4 text-sm text-page-text space-y-2">
                  <div className="font-medium">No requirement documents yet.</div>
                  <div className="text-page-muted">
                    Use <b>New requirement</b> in the rail: attach a customer spec (.docx), paste a Slack
                    message or an email, add a brief — the generator writes one standardized document
                    (Story + Gherkin + Definition of Done + Test plan) with every element quoted back
                    to its source. From the document you extract tasks and file them to the Board.
                  </div>
                </div>
              ) : tab === "spec" ? (
                <div className="rounded-lg border border-page-divider p-4 text-sm text-page-text space-y-2">
                  <div className="font-medium">No specification yet.</div>
                  <div className="text-page-muted">
                    Use <b>Generate specification</b> in the rail: the project assistant reads the project's
                    actual state and its request history and writes the spec pages here. From then on it
                    keeps them current on every change (audit-enforced).
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-page-divider p-4 text-sm text-page-text space-y-2">
                  <div className="font-medium">The knowledge base is empty.</div>
                  <div className="text-page-muted">
                    Agents write concept notes here as they work on the project. Connected sources
                    (Slack digests, meeting summaries) will land here too — no setup needed on this page.
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </main>

      {intake && (
        <RequirementIntake
          mode={intake.mode}
          docSlug={intake.slug || null}
          docTitle={intake.title || null}
          projectSlug={projectSlug}
          onClose={() => setIntake(null)}
          onDone={async (r) => {
            // Generation is BOOKED, not finished: the dialog closes now and the
            // document appears in the list when it is written. Waiting here was
            // the old behaviour and it left people watching a spinner with no
            // way to see a failure.
            const target = intake.slug || r?.slug;
            setIntake(null);
            loadReqJobs();
            if (r?.job_id) return;          // nothing to open yet
            await refresh();
            if (target) {
              // A rebuild lands the reader on the NEW version's diff, which is
              // the thing they uploaded sources to see.
              setParams(r?.rev && intake.mode === "add"
                ? { tab: "req", doc: target, rev: String(r.rev) }
                : { tab: "req", doc: target });
              try {
                const fresh = await eng("get", { slug: target });
                if (fresh?.data && !Array.isArray(fresh.data)) setDoc(fresh.data);
              } catch { /* the refresh above already relisted */ }
            }
          }}
        />
      )}
    </div>
  );
}
